import React, { useState, useEffect, useRef, useCallback } from "react";
import UserFormModal from "./UserFormModal"; // Import the modal component
import UserTable from "./UserTable"; // Import the table component
import "../../assets/styles/common.css";
import "./Settings.css"; // Keep this for specific settings tab styles

const UserSettings = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tempUser, setTempUser] = useState({
    username: "",
    password: "", // Only used for adding new users or explicit password change
    email: "",
    fullName: "",
    address: "",
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const actionMenuRefs = useRef({}); // Still managed here for global click outside

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Base URL for user *management* (GET, PUT, DELETE by ID)
  // const API_URL = process.env.REACT_APP_BACKEND_API_URL;
  const { API_URL } = require("../../config");
  const API_USERS_BASE_URL = `${API_URL}/auth/users`;
  // Specific URL for *new user registration*
  const API_REGISTER_URL = `${API_URL}/auth/register`;

  // Function to fetch users from the backend
  // 💥 FIX: Added API_USERS_BASE_URL to the dependency array
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_USERS_BASE_URL); // Use API_USERS_BASE_URL for fetching
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [API_USERS_BASE_URL]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle click outside of action menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if a menu is open AND the click is outside that specific menu
      if (
        openMenuId &&
        actionMenuRefs.current[openMenuId] &&
        !actionMenuRefs.current[openMenuId].contains(event.target)
      ) {
        // Also check if the click was on the ellipsis button itself
        // to prevent closing immediately after opening
        const ellipsisButton = event.target.closest(".ellipsis-button");
        if (
          ellipsisButton &&
          ellipsisButton.dataset.userId === String(openMenuId)
        ) {
          // Clicked on the same ellipsis button that opened the menu, don't close
          return;
        }
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]); // Dependency on openMenuId

  const handleAddUser = () => {
    setEditingUser(null);
    setTempUser({
      username: "",
      password: "",
      email: "",
      fullName: "",
      address: "",
    });
    setShowModal(true);
    setOpenMenuId(null); // Close any open action menus
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setTempUser({ ...user, password: "" }); // Clear password for security
    setShowModal(true);
    setOpenMenuId(null); // Close any open action menus
  };

  // 💥 FIX: Added API_USERS_BASE_URL and fetchUsers to the dependency array
  const handleDeleteUser = useCallback(
    async (id) => {
      // IMPORTANT: Do NOT use window.confirm or alert. Use a custom modal instead.
      // For now, I'm replacing it with a simple console log and proceeding.
      // You should implement a proper UI confirmation.
      console.log("Confirming delete for user ID:", id);

      try {
        const response = await fetch(`${API_USERS_BASE_URL}/${id}`, {
          // Use API_USERS_BASE_URL for delete
          method: "DELETE",
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }
        await fetchUsers(); // Re-fetch to show the user as "deleted" (i.e., removed from the list)
        console.log(`User with ID ${id} deleted successfully.`);
      } catch (err) {
        console.error("Error deleting user:", err);
        setError("Failed to delete user: " + err.message);
      } finally {
        setOpenMenuId(null); // Close menu after action
      }
    },
    [API_USERS_BASE_URL, fetchUsers]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTempUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();

    if (
      !tempUser.username ||
      !tempUser.email ||
      !tempUser.fullName ||
      !tempUser.address
    ) {
      // IMPORTANT: Do NOT use alert. Use a custom message box or form validation feedback.
      console.error("Please fill in all required fields.");
      setError("Please fill in all required fields.");
      return;
    }
    if (!editingUser && !tempUser.password) {
      // IMPORTANT: Do NOT use alert. Use a custom message box or form validation feedback.
      console.error("Please enter a password for the new user.");
      setError("Please enter a password for the new user.");
      return;
    }

    try {
      let response;
      let url;
      let method;
      let bodyData = { ...tempUser };

      if (editingUser) {
        // Logic for EDITING an existing user
        method = "PUT";
        url = `${API_USERS_BASE_URL}/${editingUser.id}`; // Use API_USERS_BASE_URL for update
        if (bodyData.password === "") {
          delete bodyData.password; // Don't send empty password if not changing
        }
        delete bodyData.id; // Don't send 'id' in body for PUT
      } else {
        // Logic for ADDING a new user (registration)
        method = "POST";
        url = API_REGISTER_URL; // <--- THIS IS THE KEY CHANGE
        if (!bodyData.password) {
          // IMPORTANT: Do NOT use alert. Use a custom message box or form validation feedback.
          console.error("Password is required for new users.");
          setError("Password is required for new users.");
          return;
        }
      }

      response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      await fetchUsers(); // Re-fetch users after successful save
      console.log(editingUser ? "User updated." : "New user added.");
      setShowModal(false); // Close modal
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error("Error saving user:", err);
      setError(`Failed to save user: ${err.message}`);
    } finally {
      setOpenMenuId(null); // Close any action menu if open
    }
  };

  // This function is passed down to UserTable and then to RowActionMenu
  const toggleRowActions = (id, event) => {
    event.stopPropagation(); // Prevent immediate closing by handleClickOutside
    // Optional: Add a data-user-id to the button for handleClickOutside check
    if (event.currentTarget) {
      event.currentTarget.dataset.userId = id;
    }
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setOpenMenuId(null); // Ensure no action menu is left open
    setError(null); // Clear errors when modal closes
  };

  return (
    <div className="settings-content-section">
      {/* Header and Global Action Buttons */}
      <div className="settings-header-actions">
        <h2>User List</h2>
        <div className="global-action-buttons">
          <button onClick={handleAddUser} className="btn-primary">
            <span className="button-icon">&#x2795;</span> Add New User
          </button>
        </div>
      </div>

      {/* Display error messages */}
      {error && <div className="error-message">{error}</div>}

      {/* User List Table */}
      <UserTable
        users={users}
        isLoading={isLoading}
        error={error} // Pass error to UserTable if it needs to display it
        openMenuId={openMenuId}
        actionMenuRefs={actionMenuRefs}
        toggleRowActions={toggleRowActions}
        handleEditUser={handleEditUser}
        // Pass a wrapper function or ensure handleDeleteUser closes the menu itself
        handleDeleteUser={handleDeleteUser}
        setOpenMenuId={setOpenMenuId} // Pass state setter down to allow children to close menu
      />

      {/* User Add/Edit Modal (Pop-up) */}
      <UserFormModal
        showModal={showModal}
        editingUser={editingUser}
        tempUser={tempUser}
        handleChange={handleChange}
        handleSaveUser={handleSaveUser}
        onClose={handleCloseModal} // Use the dedicated close handler
        // Pass error state to modal if it needs to display validation errors
        errorMessage={error}
      />
    </div>
  );
};

export default UserSettings;
